import {
  AfterViewInit,
  Component, ContentChild, ElementRef, EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Optional, Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash-es';
import { TableState } from 'primeng/api';
import { OverlayPanel } from 'primeng/overlaypanel';
import { Table, TableService } from 'primeng/table';
import { ReplaySubject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { translationKeys } from '../../sharedTranslationKeys';
import { AvanoTableFilteringService } from './avano-table-filtering.service';
import { onColumnResizeEnd, resizeTableCellsOverwrite, restoreColumnWidthsOverwrite, restoreStateBugfixed, saveStateWithoutSelection } from './avano-table-primeng-overwrites';
import { ColumnVisibilityTriggeredEvent } from './avano-table-settings/avano-table-settings.component';
import { AvanoTableStateService } from './avano-table-state.service';
import { TableColumnConfiguration, TableConfiguration } from './avano-table.model';


export abstract class TableExportService {
  public doExport: (avanoTable: AvanoTableComponent) => void = undefined;
}
@Component({
  selector: 'avano-table',
  templateUrl: './avano-table.component.html',
  styleUrls: ['./avano-table.component.scss'],
  providers: [AvanoTableFilteringService, AvanoTableStateService]
})
export class AvanoTableComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() tableCollapsed = false;
  @Input() initialRowsPerPage = 50;
  @Input() headerText: string;
  @Input() defaultRowsPerPageOptions = [10, 25,  50, 100, 250, 500];
  tableTranslationKeys = translationKeys.AvanoTable
  private _tableRef: CustomizedTable;
  @Input() set tableRef(newTable: CustomizedTable) {
    if (newTable !== this._tableRef) {
      this._tableRef = newTable as CustomizedTable;
      this.filteringService.init(newTable);
      this.hookToOnColumnsChange(newTable.tableService as CustomizedTableService);
    }
  }
  get tableRef(): CustomizedTable { return this._tableRef; }

  @Input()
  panelTemplates: {panelContentActions: TemplateRef<any>};

  private _tableConfiguration: TableConfiguration = { useTableDefaults: true};
  @Input()
  set tableConfiguration(newConfiguration: TableConfiguration) {
    if (newConfiguration !== this._tableConfiguration) {
      this._tableConfiguration = newConfiguration;
    }
  }
  get tableConfiguration(): TableConfiguration {
    return this._tableConfiguration;
  }

  @Output() readonly export: EventEmitter<void> = new EventEmitter<void>();
  @Output() readonly refresh: EventEmitter<void> = new EventEmitter<void>();

  @Input()
  set singleColumnChange(newColumnConfiguration: TableColumnConfiguration) {
    if (newColumnConfiguration) {
      const foundColumn = this.tableRef.findColumnByKey(newColumnConfiguration.field);
      if (foundColumn) {
        this.changeColumnVisibility({newVisibility: newColumnConfiguration.show, columnField: newColumnConfiguration.field});
      }
    }
  }

  @ViewChild('tableSettings') tableSettingsMenu: OverlayPanel;
  @ContentChild(Table) tabelRef: Table;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  private _allColumns: TableColumnConfiguration[] = [];
  set allColumns(newColumns: TableColumnConfiguration[]) {
    if (newColumns !== this._allColumns) {
      this._allColumns = newColumns;
      this.resetDisplayedColumns();
      this.resetAllColumnsConfigurations();
    }
  }
  get allColumns() { return this._allColumns; }

  private get displayedColumns():TableColumnConfiguration[] { return this.tableRef.columns; }
  private set displayedColumns(newColumns: TableColumnConfiguration[]) { this.tableRef.columns = newColumns}
  public allColumnsConfigurations: TableColumnConfiguration[] = [];

  private _rowsPerPage: number;
  set rowsPerPage(newRowsPerPage: number) {
    if (this.rowsPerPage !== newRowsPerPage) {
      this.tableRef.rows = newRowsPerPage;
      this._rowsPerPage = newRowsPerPage;
      this.refreshTable();
    }
  }
  get rowsPerPage() { return this._rowsPerPage ?? this.initialRowsPerPage; }


  private _rowsPerPageOptions: number[] = this.defaultRowsPerPageOptions;
  set rowsPerPageOptions(newOptions: number[]) {
    this.tableRef.rowsPerPageOptions = newOptions;
    this._rowsPerPageOptions = newOptions;
  }
  get rowsPerPageOptions() { return this._rowsPerPageOptions ?? this.defaultRowsPerPageOptions; }

  afterViewInitialized = false
  constructor(
    @Optional() private tableExportService: TableExportService,
    private elementRef: ElementRef,
    private translateService: TranslateService,
    private filteringService: AvanoTableFilteringService,
    private tableStateService: AvanoTableStateService,
  ) { }

  ngOnInit(): void {
    this.applyShowHideColumnBehavior();
    this.tableRef._defaultSaveState = this.tableRef.saveState;
    this.tableRef.saveState = saveStateWithoutSelection.bind(this.tableRef);
    this.tableRef.restoreState = restoreStateBugfixed.bind(this.tableRef);
    this.tableRef.onColumnResizeEnd = onColumnResizeEnd.bind(this.tableRef);
    this.tableRef._resizeTableCellsOriginal = this.tableRef.resizeTableCells;
    this.tableRef.resizeTableCells = resizeTableCellsOverwrite.bind(this.tableRef);
    this.tableRef.restoreColumnWidths = restoreColumnWidthsOverwrite.bind(this.tableRef);

    const canSaveTableState = !!this.tableStateService.settingsService;
    const shouldSaveTableState = typeof this.tableConfiguration.tableSettings?.settingsDbIdentifier === 'string';
    if (canSaveTableState && shouldSaveTableState) {
      this.preventLazyLoadUntilInitialized();
    }
    if (this.tableConfiguration.useTableDefaults) {
      this.initTableWithDefaults();
    }
  }

  ngAfterViewInit(): void {
    this.afterViewInitialized = true;

    if (this.tableConfiguration.tableSettings?.settingsEnabled) {
      this.setUpColumnSizesBackup();
    }

    this.tableStateService.init(this);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private initTableWithDefaults(): void {
    if(this.tableConfiguration.withPagination) {
      this.tableRef.paginator = true;

      this.translateService.get(this.tableTranslationKeys.Footer.Pagination.PageCountWithTotalTemplate).subscribe((translatedPaginatorTemplate: string) => {
        this.tableRef.showCurrentPageReport = true;
        this.tableRef.currentPageReportTemplate = translatedPaginatorTemplate;
      });
      this.tableRef.rows = this.rowsPerPage;
      this.tableRef.rowsPerPageOptions = this.rowsPerPageOptions;
    }

    this.tableRef.reorderableColumns = true;
    this.tableRef.resizableColumns = true;
    this.tableRef.resetPageOnSort = true;
  }

  private applyShowHideColumnBehavior(): void {
    if (this.tableRef._customFindColumnByKeySet !== true) {
      this.tableRef.findColumnByKey = ((key: string) => (this.allColumns ?? []).find(col => col.field === key) ?? null);
      this.tableRef._customFindColumnByKeySet = true;
    }
  }

  private resetDisplayedColumns() {
    this.displayedColumns = this.allColumns
    .filter((column: TableColumnConfiguration) => column.show !== false)
    .map(col => ({...col}));
  }

  private resetAllColumnsConfigurations() {
    this.allColumnsConfigurations = this.allColumns
      .filter((column) => !column.userHidden)
      .map(col => ({...col}));
  }

  exportFunction = () => {
    if(this.tableExportService) {
      this.tableExportService.doExport(this);
    }
    this.export.emit();
  };

  refreshFunction = () => {
    // Triggers the backend call of the respective component to refresh with the table's lazyload event
    this.tableRef.onLazyLoad.emit(this.tableRef.createLazyLoadMetadata());
    this.refresh.emit();
  };

  openConfiguration = (event, target) => {
    this.initSettingsOverlay();
    this.tableSettingsMenu.toggle(event, target);
  };

  private initSettingsOverlay() {
    this.allColumnsConfigurations.forEach((singleColumn: TableColumnConfiguration) => {
      const matchingTableRefColumn = this.displayedColumns.find((currentVisibleColumn: TableColumnConfiguration) => {
        return currentVisibleColumn.field === singleColumn.field;
      });
      singleColumn.show = matchingTableRefColumn !== undefined;
    });
  }

  changeColumnVisibility({newVisibility: isVisible, columnField}: ColumnVisibilityTriggeredEvent) {
    const userColumn = this.allColumnsConfigurations.find((singleColumn: TableColumnConfiguration) => singleColumn.field === columnField);
    if (userColumn) {
      userColumn.show = isVisible;
    }

    const currentColumn = this.displayedColumns.find((singleColumn: TableColumnConfiguration) => singleColumn.field === columnField);

    if (isVisible && !currentColumn) {
      const originalPosition = this.allColumns.findIndex((singleColumn: TableColumnConfiguration) => singleColumn.field === columnField);
      if (originalPosition >= 0 ) {
        const originalColumn = this.allColumns[originalPosition];
        originalColumn.show = isVisible;
        this.displayedColumns.splice(originalPosition, 0, originalColumn);
      }
    } else if (!isVisible && currentColumn) {
      const columnsWithoutCurrentColumn = this.displayedColumns.filter((singleColumn: TableColumnConfiguration) => singleColumn.field !== columnField);
      this.displayedColumns = columnsWithoutCurrentColumn;
    }

    this.tableRef.saveState();
  }

  public resetTableSettings(): void {
    if(this.tableConfiguration.withPagination) {
      const previousRowsPerPage = this.rowsPerPage;
      this.rowsPerPage = this.initialRowsPerPage;

      const rowsPerPageChanged = previousRowsPerPage !== this.rowsPerPage;
      if(rowsPerPageChanged) {
        this.tableRef.onLazyLoad.emit(this.tableRef.createLazyLoadMetadata());
      }
    }

    this.tableRef.filters = {};

    this.resetDisplayedColumns();

    this.tableRef.clearState()
    this.tableRef.columnWidthsState = this.tableRef._initialColumnSizes;
    this.tableRef.tableWidthState = this.tableRef._initialTableWidth;
    this.tableRef.restoreColumnWidths();

    this.initSettingsOverlay();
    this.tableRef.onStateSave.emit(undefined);
  }
  private preventLazyLoadUntilInitialized() {
    this.tableRef._customRestoreStateCalled = false;
    this.tableRef._defaultOnLazyLoad = this.tableRef._defaultOnLazyLoad ?? this.tableRef.onLazyLoad;

    this.tableRef.onLazyLoad = new EventEmitter();
    this.tableRef.onLazyLoad.pipe(
      filter(() => this.tableRef._customRestoreStateCalled === true),
      takeUntil(this.destroyed$))
    .subscribe((event) => this.tableRef._defaultOnLazyLoad.emit(event));
  }

  private refreshTable(): void {
    if (!this.afterViewInitialized) return;
    // We cannot use "reset()" as this would clear filter/sort, but simulate "click on first page" is refreshing
    // as we would like. (and its the same what happens in the default paginator rowsPerPage-dropdown ;))
    this.tableRef.onPageChange({ first: 0, rows: this.rowsPerPage });
  }

  private hookToOnColumnsChange(tableService: CustomizedTableService) {
      tableService._defaultOnColumnsChange = tableService._defaultOnColumnsChange ?? tableService.onColumnsChange.bind(tableService);

      tableService.onColumnsChange = (newColumns: TableColumnConfiguration[]) => {
        tableService._defaultOnColumnsChange(newColumns);
        if (!isEqual(newColumns, this.allColumns)) {
          this.allColumns = newColumns;
        }
      }
  }

  private backupInitialColumSizes(): void {
    const columnAndTableWidths: TableState = { };
    this.tableRef.saveColumnWidths(columnAndTableWidths);
    this.tableRef._initialColumnSizes = columnAndTableWidths.columnWidths;
    this.tableRef._initialTableWidth = columnAndTableWidths.tableWidth;
  }

  private setUpColumnSizesBackup() {
    const avanoTableNativeElement: HTMLElement = this.elementRef?.nativeElement;
    if (!this.afterViewInitialized || !avanoTableNativeElement) {
      console.warn("View not initialized"); return;
    }
    const tableHtmlElement: HTMLElement = this.tableRef?.tableViewChild?.nativeElement;
    if (!tableHtmlElement) {
      console.warn("Table native Element not set: "); return;
    }
    /**
     * AfterViewInit in some cases is called before Table actually gets rendered in DOM
     * That resulted in column/table widths being backupped with wrong values, sometimes ~10px difference.
     */
    const observer = new MutationObserver(() => {
      if (avanoTableNativeElement.contains(tableHtmlElement)) {
          this.backupInitialColumSizes();
          observer.disconnect();
        }
    });
    observer.observe(avanoTableNativeElement, { childList: true, subtree: true});
  }
}

export interface CustomizedTableService extends TableService {
  _defaultOnColumnsChange: (columns: TableColumnConfiguration[]) => void;
}

export interface CustomizedTable extends Table {
  _defaultRestoreState?: () => void;
  _defaultSaveState?: () => void;
  _resizeTableCellsOriginal?: (newColumnWidth: any, nextColumnWidth: any) => void;
  _defaultOnLazyLoad?: EventEmitter<any>;
  _customFindColumnByKeySet?: boolean;
  _customRestoreStateCalled?: boolean;
  _initialColumnSizes?: string;
  _initialTableWidth?: string;
}
